import { useEffect, useState, useMemo } from "react";
import {
  collection, query, onSnapshot, writeBatch,
  doc, addDoc, serverTimestamp, where
} from "firebase/firestore";
import { db } from "@/lib/firebase";
import { useUser } from "@/lib/useUser";
import { useLanguage } from "@/contexts/LanguageContext";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Send, AlertCircle, TrendingUp, DollarSign,
  Clock, CheckCircle, FileText, ChevronRight,
  Building2, Eye
} from "lucide-react";
import { toast } from "sonner";
import { NavLink } from "react-router-dom";

// ── Palette ───────────────────────────────────────────────────────────────────
const NAVY = "#1D314D";
const GOLD = "#CEAB65";

// ── Types ─────────────────────────────────────────────────────────────────────
type SubStatus = "draft" | "ready" | "published" | "rejected" | "countered";

interface Submission {
  id: string;
  dealId: string;
  amount: number;
  rate: number;
  amortYears: number;
  conditions?: string[];
  lenderLegalName?: string;
  contactEmail?: string;
  createdAt?: any;
  status?: SubStatus;
  businessName?: string;
}

// ── Translations ──────────────────────────────────────────────────────────────
const T = {
  en: {
    title: "Pipeline",
    subtitle: "Manage and publish your loan offer submissions",
    totalSubmissions: "Total Submissions", drafts: "Drafts",
    published: "Published", totalValue: "Total Value",
    identity: "Identity", identityDesc: "Used for private submissions only — never shown publicly",
    legalName: "Legal Name", legalNamePlaceholder: "Your institution's legal name",
    contactEmail: "Contact Email",
    submissions: "My Submissions", submissionsDesc: "submission(s) ready to publish",
    noSubmissions: "No submissions yet",
    noSubmissionsSub: "Submit a loan offer from the Deal Monitor to get started",
    dealId: "Deal", amount: "Amount", rate: "Rate", term: "Term",
    conditions: "Conditions", publish: "Publish", published2: "Published",
    years: "yrs", fillIdentity: "Fill in legal name and email before publishing",
    maskedAs: "Public masked name",
    statusDraft: "Draft", statusReady: "Ready", statusPublished: "Published",
    statusRejected: "Rejected", statusCountered: "Counter-offer",
    publishSuccess: "Offer published — identity masked in public view",
    publishError: "Failed to publish",
    loginRequired: "Please log in with a lender account",
    viewDeal: "View deal",
    kanbanDraft: "Draft", kanbanReady: "Ready to publish", kanbanPublished: "Published",
  },
  fr: {
    title: "Pipeline",
    subtitle: "Gérez et publiez vos soumissions d'offres de prêt",
    totalSubmissions: "Total Soumissions", drafts: "Brouillons",
    published: "Publiées", totalValue: "Valeur Totale",
    identity: "Identité", identityDesc: "Utilisée pour les soumissions privées seulement — jamais affichée publiquement",
    legalName: "Nom légal", legalNamePlaceholder: "Nom légal de votre institution",
    contactEmail: "Courriel de contact",
    submissions: "Mes Soumissions", submissionsDesc: "soumission(s) prête(s) à publier",
    noSubmissions: "Aucune soumission pour l'instant",
    noSubmissionsSub: "Soumettez une offre depuis le Deal Monitor pour commencer",
    dealId: "Deal", amount: "Montant", rate: "Taux", term: "Durée",
    conditions: "Conditions", publish: "Publier", published2: "Publiée",
    years: "ans", fillIdentity: "Remplissez le nom légal et le courriel avant de publier",
    maskedAs: "Nom masqué public",
    statusDraft: "Brouillon", statusReady: "Prête", statusPublished: "Publiée",
    statusRejected: "Refusée", statusCountered: "Contre-offre",
    publishSuccess: "Offre publiée — identité masquée dans la vue publique",
    publishError: "Échec de la publication",
    loginRequired: "Veuillez vous connecter avec un compte prêteur",
    viewDeal: "Voir le deal",
    kanbanDraft: "Brouillon", kanbanReady: "Prête à publier", kanbanPublished: "Publiée",
  },
};

// ── Status config ─────────────────────────────────────────────────────────────
const STATUS_CFG: Record<SubStatus, { labelKey: keyof typeof T.en; color: string }> = {
  draft:      { labelKey: "statusDraft",      color: "bg-gray-100 text-gray-700 border-gray-200" },
  ready:      { labelKey: "statusReady",      color: "bg-amber-50 text-amber-800 border-amber-200" },
  published:  { labelKey: "statusPublished",  color: "bg-green-50 text-green-800 border-green-200" },
  rejected:   { labelKey: "statusRejected",   color: "bg-red-50 text-red-800 border-red-200" },
  countered:  { labelKey: "statusCountered",  color: "bg-purple-50 text-purple-800 border-purple-200" },
};

// ── Helpers ───────────────────────────────────────────────────────────────────
const fmt = (n: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n);

// ── Stat card ─────────────────────────────────────────────────────────────────
function StatCard({ label, value, sub, icon: Icon, accent }: {
  label: string; value: string | number; sub: string;
  icon: React.ElementType; accent: string;
}) {
  return (
    <div
      className="bg-card rounded-lg p-4 relative overflow-hidden"
      style={{ border: "0.5px solid var(--color-border-tertiary)", borderTop: `2.5px solid ${accent}` }}
    >
      <div className="flex items-start justify-between mb-3">
        <p className="text-[10.5px] uppercase tracking-wide text-muted-foreground">{label}</p>
        <Icon className="w-4 h-4" style={{ color: accent }} />
      </div>
      <p className="text-2xl font-medium tracking-tight" style={{ color: "var(--color-text-primary)" }}>{value}</p>
      <p className="text-[10px] text-muted-foreground mt-1">{sub}</p>
    </div>
  );
}

// ── Kanban column ─────────────────────────────────────────────────────────────
function KanbanColumn({ title, count, accent, children }: {
  title: string; count: number; accent: string; children: React.ReactNode;
}) {
  return (
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-2 mb-3 px-1">
        <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: accent }} />
        <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{title}</span>
        <span
          className="ml-auto text-[10px] px-1.5 py-0.5 rounded-full font-medium"
          style={{ background: `${accent}18`, color: accent }}
        >
          {count}
        </span>
      </div>
      <div className="space-y-2 min-h-[120px]">
        {children}
      </div>
    </div>
  );
}

// ── Kanban card ───────────────────────────────────────────────────────────────
function KanbanCard({ sub, t, onPublish, legalName, contactEmail, isFr }: {
  sub: Submission;
  t: typeof T.en;
  onPublish: (sub: Submission) => void;
  legalName: string;
  contactEmail: string;
  isFr: boolean;
}) {
  const status = sub.status ?? "draft";
  const cfg = STATUS_CFG[status];
  const isPublished = status === "published";

  return (
    <div
      className="rounded-lg p-3 transition-shadow hover:shadow-sm"
      style={{
        background: "var(--color-background-primary)",
        border: "0.5px solid var(--color-border-tertiary)",
        borderLeft: `3px solid ${isPublished ? "#639922" : status === "ready" ? GOLD : "#B4B2A9"}`,
      }}
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex items-center gap-1.5 min-w-0">
          <Building2 className="w-3 h-3 flex-shrink-0 text-muted-foreground" />
          <span className="text-xs font-medium truncate" style={{ color: NAVY }}>
            {sub.businessName || `Deal ${sub.dealId.slice(0, 8)}…`}
          </span>
        </div>
        <span className={`text-[9px] px-1.5 py-0.5 rounded border font-medium flex-shrink-0 ${cfg.color}`}>
          {t[cfg.labelKey] as string}
        </span>
      </div>

      <div className="grid grid-cols-3 gap-1 mb-2.5">
        {[
          { label: t.amount, value: fmt(sub.amount) },
          { label: t.rate,   value: `${sub.rate}%` },
          { label: t.term,   value: `${sub.amortYears} ${t.years}` },
        ].map((f, i) => (
          <div key={i}>
            <p className="text-[9px] text-muted-foreground">{f.label}</p>
            <p className="text-[11px] font-medium">{f.value}</p>
          </div>
        ))}
      </div>

      {sub.conditions && sub.conditions.length > 0 && (
        <p className="text-[9px] text-muted-foreground mb-2 line-clamp-1">
          {sub.conditions.join(" · ")}
        </p>
      )}

      <div className="flex items-center gap-1.5">
        <NavLink to={`/lender/deal-preview/${sub.dealId}`} className="flex-shrink-0">
          <button
            className="text-[10px] flex items-center gap-1 px-2 py-1 rounded transition-all"
            style={{ color: "var(--color-text-secondary)", border: "0.5px solid var(--color-border-tertiary)" }}
          >
            <Eye className="h-2.5 w-2.5" />
            {t.viewDeal}
          </button>
        </NavLink>
        {!isPublished && (
          <button
            onClick={() => onPublish(sub)}
            className="flex-1 text-[10px] flex items-center justify-center gap-1 px-2 py-1 rounded transition-all"
            style={{ background: NAVY, color: GOLD, border: `1px solid rgba(206,171,101,0.25)` }}
          >
            <Send className="h-2.5 w-2.5" />
            {t.publish}
          </button>
        )}
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function LenderPipeline() {
  const { user, role } = useUser();
  const { language } = useLanguage();
  const isFr = language === "fr";
  const t = T[language as "en" | "fr"] ?? T.en;

  const [submissions, setSubmissions] = useState<Submission[]>([]);
  const [loading, setLoading] = useState(true);
  const [legalName, setLegalName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [publishing, setPublishing] = useState<string | null>(null);

  useEffect(() => {
    if (!user || !["lender", "admin", "staff"].includes(role)) {
      setLoading(false);
      return;
    }
    const q = query(collection(db, `lenders/${user.id}/submissions`));
    const unsub = onSnapshot(q, (snap) => {
      setSubmissions(snap.docs.map(d => ({ id: d.id, ...d.data() } as Submission)));
      setLoading(false);
    }, () => { setLoading(false); });
    return () => unsub();
  }, [user, role]);

  const maskedName = useMemo(() => {
    if (!user) return "Lender #???";
    return `Lender #${user.id.slice(0, 4).toUpperCase()}`;
  }, [user]);

  // Stats
  const stats = useMemo(() => ({
    total:     submissions.length,
    drafts:    submissions.filter(s => !s.status || s.status === "draft" || s.status === "ready").length,
    published: submissions.filter(s => s.status === "published").length,
    value:     submissions.reduce((sum, s) => sum + (s.amount ?? 0), 0),
  }), [submissions]);

  // Kanban buckets
  const draftSubs     = useMemo(() => submissions.filter(s => !s.status || s.status === "draft"), [submissions]);
  const readySubs     = useMemo(() => submissions.filter(s => s.status === "ready"), [submissions]);
  const publishedSubs = useMemo(() => submissions.filter(s => s.status === "published" || s.status === "rejected" || s.status === "countered"), [submissions]);

  // Publish handler (preserved from original)
  const publishAnonymousOffer = async (submission: Submission) => {
    if (!user) { toast.error("Not authenticated"); return; }
    if (!legalName || !contactEmail) { toast.error(t.fillIdentity); return; }

    setPublishing(submission.id);
    try {
      const batch = writeBatch(db);
      const tempRef = await addDoc(collection(db, "_tmp_ids"), { at: serverTimestamp() });
      const newId = tempRef.id;

      batch.set(doc(db, `deals/${submission.dealId}/loanOffers_public/${newId}`), {
        amount: submission.amount, rate: submission.rate,
        amortYears: submission.amortYears, conditions: submission.conditions || [],
        createdAt: serverTimestamp(), status: "open",
        lenderMaskedName: maskedName,
      });

      batch.set(doc(db, `deals/${submission.dealId}/loanOffers_private/${newId}`), {
        amount: submission.amount, rate: submission.rate,
        amortYears: submission.amortYears, conditions: submission.conditions || [],
        createdAt: serverTimestamp(), status: "open",
        lenderId: user.id, lenderLegalName: legalName, contactEmail,
      });

      batch.set(
        doc(db, `lenders/${user.id}/submissions/${submission.id}`),
        { status: "published", publishedAt: serverTimestamp() },
        { merge: true }
      );

      await batch.commit();
      toast.success(t.publishSuccess);
    } catch (err: any) {
      toast.error(`${t.publishError}: ${err.message}`);
    } finally {
      setPublishing(null);
    }
  };

  // ── Guards ────────────────────────────────────────────────────────────────
  if (!user) return (
    <Card>
      <CardContent className="pt-6">
        <div className="text-center text-muted-foreground py-10">
          <AlertCircle className="h-10 w-10 mx-auto mb-3 opacity-40" />
          <p className="text-sm">{t.loginRequired}</p>
        </div>
      </CardContent>
    </Card>
  );

  if (loading) return (
    <div className="flex items-center justify-center min-h-[400px]">
      <div className="animate-spin rounded-full h-7 w-7" style={{ borderBottom: `2px solid ${NAVY}` }} />
    </div>
  );

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-6">

      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-medium" style={{ color: NAVY }}>{t.title}</h1>
          <p className="text-sm text-muted-foreground mt-0.5">{t.subtitle}</p>
        </div>
        <NavLink to="/lender/submit-offer">
          <Button size="sm" className="gap-2" style={{ background: NAVY, color: GOLD, border: `1px solid rgba(206,171,101,0.3)` }}>
            <Send className="h-4 w-4" />
            {isFr ? "Nouvelle offre" : "New Offer"}
          </Button>
        </NavLink>
      </div>

      {/* Stat cards */}
      <div className="grid gap-3 grid-cols-2 md:grid-cols-4">
        <StatCard label={t.totalSubmissions} value={stats.total}          sub={isFr ? "soumissions" : "submissions"} icon={FileText}   accent={GOLD} />
        <StatCard label={t.drafts}           value={stats.drafts}         sub={isFr ? "en attente" : "pending"}      icon={Clock}      accent="#BA7517" />
        <StatCard label={t.published}        value={stats.published}      sub={isFr ? "publiées" : "published"}      icon={CheckCircle} accent="#639922" />
        <StatCard label={t.totalValue}       value={fmt(stats.value)}     sub={isFr ? "valeur totale" : "total value"} icon={DollarSign} accent={NAVY} />
      </div>

      {/* Identity card */}
      <Card style={{ border: "0.5px solid var(--color-border-tertiary)" }}>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <span className="w-1.5 h-1.5 rounded-full" style={{ background: GOLD }} />
            {t.identity}
          </CardTitle>
          <CardDescription className="text-xs">{t.identityDesc}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="legalName" className="text-xs">{t.legalName}</Label>
              <Input
                id="legalName"
                placeholder={t.legalNamePlaceholder}
                value={legalName}
                onChange={e => setLegalName(e.target.value)}
                className="h-9 text-sm"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="contactEmail" className="text-xs">{t.contactEmail}</Label>
              <Input
                id="contactEmail"
                type="email"
                placeholder="contact@institution.com"
                value={contactEmail}
                onChange={e => setContactEmail(e.target.value)}
                className="h-9 text-sm"
              />
            </div>
          </div>
          <p className="text-[10px] text-muted-foreground mt-3">
            {t.maskedAs} : <span className="font-mono font-medium">{maskedName}</span>
          </p>
        </CardContent>
      </Card>

      {/* Kanban */}
      {submissions.length === 0 ? (
        <Card style={{ border: "0.5px solid var(--color-border-tertiary)" }}>
          <CardContent className="py-16 text-center">
            <TrendingUp className="h-10 w-10 mx-auto mb-3 text-muted-foreground/25" />
            <p className="text-sm font-medium text-muted-foreground">{t.noSubmissions}</p>
            <p className="text-xs text-muted-foreground mt-1">{t.noSubmissionsSub}</p>
            <NavLink to="/listings">
              <Button variant="outline" size="sm" className="mt-4 gap-2">
                <ChevronRight className="h-4 w-4" />
                {isFr ? "Voir les deals" : "Browse Deals"}
              </Button>
            </NavLink>
          </CardContent>
        </Card>
      ) : (
        <Card style={{ border: "0.5px solid var(--color-border-tertiary)" }}>
          <CardHeader className="pb-4">
            <CardTitle className="text-base flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: GOLD }} />
              {t.submissions}
            </CardTitle>
            <CardDescription className="text-xs">
              {readySubs.length} {t.submissionsDesc}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex gap-4">
              {/* Drafts column */}
              <KanbanColumn title={t.kanbanDraft} count={draftSubs.length} accent="#B4B2A9">
                {draftSubs.length === 0
                  ? <p className="text-[11px] text-muted-foreground px-1">{isFr ? "Aucun brouillon" : "No drafts"}</p>
                  : draftSubs.map(s => (
                    <KanbanCard key={s.id} sub={s} t={t} isFr={isFr}
                      onPublish={publishAnonymousOffer}
                      legalName={legalName} contactEmail={contactEmail} />
                  ))}
              </KanbanColumn>

              {/* Divider */}
              <div className="w-px bg-border flex-shrink-0" />

              {/* Ready column */}
              <KanbanColumn title={t.kanbanReady} count={readySubs.length} accent={GOLD}>
                {readySubs.length === 0
                  ? <p className="text-[11px] text-muted-foreground px-1">{isFr ? "Aucune prête" : "None ready"}</p>
                  : readySubs.map(s => (
                    <KanbanCard key={s.id} sub={s} t={t} isFr={isFr}
                      onPublish={publishAnonymousOffer}
                      legalName={legalName} contactEmail={contactEmail} />
                  ))}
              </KanbanColumn>

              {/* Divider */}
              <div className="w-px bg-border flex-shrink-0" />

              {/* Published column */}
              <KanbanColumn title={t.kanbanPublished} count={publishedSubs.length} accent="#639922">
                {publishedSubs.length === 0
                  ? <p className="text-[11px] text-muted-foreground px-1">{isFr ? "Aucune publiée" : "None published"}</p>
                  : publishedSubs.map(s => (
                    <KanbanCard key={s.id} sub={s} t={t} isFr={isFr}
                      onPublish={publishAnonymousOffer}
                      legalName={legalName} contactEmail={contactEmail} />
                  ))}
              </KanbanColumn>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}